import {
  CircularProgress,
  LinearProgress,
  Table,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@bb-ui/react-library';
import * as React from 'react';
import { useTranslation } from 'react-i18next';
import { IJobSummary, ListJobsResponseBody } from '@lct-jobs/core';
import { useEffect } from 'react';
import useRestApi from '../../hooks/useRestApi';
import { apiUrl } from '../../utils/apiUrl';
import { JobRow } from './JobRow';
import { useTenantContext } from '../../contexts/TenantContext';
import { useSnackbar } from '../../hooks/useSnackbar';
import { useLearnInstance } from 'hooks/useLearnInstance';
import { useJobRegion } from 'contexts/JobRegionContext';
import { useStyles } from './useStyles';
import { JobRegionPicker } from 'pages/Tenant/JobRegionPicker/JobRegionPicker';

const commonJobId = ['OnetimeLogin', 'UpdateTenantState', 'UpgradeLearnInstance'];

export const JobList: React.FC = () => {
  const { t } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();
  const { tenant, tenantId } = useTenantContext();
  const { showRegionPicker, lctApiUrl, isJobRegionReady } = useJobRegion();
  const classes = useStyles();

  const jobApi = useRestApi<ListJobsResponseBody>(
    lctApiUrl(`tenants/${tenantId}/jobManager/jobs`),
    { manual: !isJobRegionReady },
  );

  const { learnInstanceId } = useLearnInstance();

  const jobApiChildTenant = useRestApi<ListJobsResponseBody>(
    apiUrl('lct', `tenants/${learnInstanceId}/jobManager/jobs`),
    { manual: true },
  );

  const [jobs, setJobs] = React.useState<IJobSummary[]>([]);
  const [searchQuery, setSearchQuery] = React.useState<string>('');
  const [hasFetchedChildJobs, setHasFetchedChildJobs] = React.useState<boolean>(false);

  const { data, error } = jobApi;

  useEffect(() => {
    if (learnInstanceId && tenant?.tenantType === 'Learn' && !hasFetchedChildJobs) {
      jobApiChildTenant.fetch();
      setHasFetchedChildJobs(true);
    }
  }, [hasFetchedChildJobs, jobApiChildTenant, tenant, learnInstanceId]);

  useEffect(() => {
    const combinedJobs: IJobSummary[] = [
      ...(data?.jobs ?? []),
      ...(jobApiChildTenant.data?.jobs ?? []),
    ];

    const commonJobs = combinedJobs.filter((job) => commonJobId.includes(job.jobId));
    const deleteJobs = combinedJobs.filter((job) => job.jobId.toLowerCase().startsWith('delete'));
    const otherJobs = combinedJobs.filter(
      (job) => !commonJobId.includes(job.jobId) && !job.jobId.toLowerCase().startsWith('delete'),
    );

    setJobs([...commonJobs, ...otherJobs, ...deleteJobs]);
  }, [data, jobApiChildTenant.data, tenant]);

  useEffect(() => {
    if (error) {
      enqueueSnackbar(`Failed to load Jobs: ${error?.message}`, { variant: 'error' });
    }
  }, [enqueueSnackbar, error]);

  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchQuery(event.target.value);
  };

  const filteredJobs = jobs.filter(
    (job) =>
      (job.jobId?.toLowerCase() || '').includes(searchQuery.toLowerCase()) ||
      (job.jobName?.toLowerCase() || '').includes(searchQuery.toLowerCase()) ||
      (job.jobDescription?.toLowerCase() || '').includes(searchQuery.toLowerCase()),
  );

  if (!isJobRegionReady) return <LinearProgress ariaLabel="Loading Jobs" />;

  return (
    <div>
      <div className={classes.controlsRow}>
        <TextField
          floatingLabel={true}
          label={t('jobService.searchJobs')}
          value={searchQuery}
          onChange={handleSearchChange}
        />
        {showRegionPicker && <JobRegionPicker />}
      </div>
      {(jobApi.loading || jobApiChildTenant.loading) && jobs.length === 0 ? (
        <LinearProgress ariaLabel="Loading Jobs" />
      ) : null}
      <div className={classes.tableWrapper}>
        {(jobApi.loading || jobApiChildTenant.loading) && jobs.length > 0 && (
          <div className={classes.loadingOverlay}>
            <CircularProgress ariaLabel="Loading Jobs" />
          </div>
        )}
        <Table>
          <TableHead>
            <TableRow key="$HEADER">
              <TableCell>{t('jobService.name')}</TableCell>
              <TableCell>{t('jobService.domain')}</TableCell>
              <TableCell>{t('jobService.description')}</TableCell>
              <TableCell>{t('jobService.submitJob')}</TableCell>
            </TableRow>
          </TableHead>
          <tbody>
            {filteredJobs.length === 0 && !jobApi.loading && !jobApiChildTenant.loading ? (
              <TableRow>
                <TableCell colSpan={4} style={{ textAlign: 'center' }}>
                  <Typography>{t('jobService.noData')}</Typography>
                </TableCell>
              </TableRow>
            ) : (
              filteredJobs.map((job) => (
                <JobRow
                  key={job.jobId}
                  jobSummary={job}
                  tenantId={tenantId}
                  siteTenantId={learnInstanceId}
                />
              ))
            )}
          </tbody>
        </Table>
      </div>
    </div>
  );
};
